import { ApplicationRef, Component, inject, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { Observable, Subject, concat, filter, first, interval, map, shareReplay, takeUntil } from 'rxjs';
import { AuthService } from './services/auth.service';
import { environment } from 'src/environments/environment';
import { SwUpdate } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
  loading = true;

  isIframe = false;
  private readonly _destroying$ = new Subject<void>();

  private breakpointObserver = inject(BreakpointObserver);

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  constructor(
    private appRef: ApplicationRef,
    private updates: SwUpdate,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    public authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private snackBar: MatSnackBar,
    protected auth: AuthService
  ) {
    const appIsStable$ = appRef.isStable.pipe(first((isStable) => isStable === true));
    const everyHour$ = interval(1 * 60 * 60 * 1000);
    const everyHourOnceAppIsStable$ = concat(appIsStable$, everyHour$);

    if (updates.isEnabled)
      everyHourOnceAppIsStable$.subscribe(async () => {
        try {
          const updateFound = await updates.checkForUpdate();
          if (updateFound) {
            snackBar.open(`En nyere version er tilgængelig`, "Opdatér siden", {
              duration: 0,
              verticalPosition: 'top'
            }).onAction().subscribe(() => {
              document.location.reload();
            });
          }
        } catch (err) {
          console.error('Failed to check for updates:', err);
        }
      });

    updates.unrecoverable.subscribe(async event => {
      // Discard the broken worker before offering the reload. On its own a
      // reload replays the same corrupt caches, which is what leaves people
      // stuck reloading instead of recovering.
      await this.discardServiceWorker();

      snackBar.open(`Der er sket en fejl: ${event.reason}.`, "Opdatér siden", {
        duration: 0,
        verticalPosition: 'top',
        panelClass: 'snackbar-error'
      }).onAction().subscribe(() => {
        document.location.reload();
      });
    });
  }

  /**
   * Unregisters the service worker and drops its caches, so the next load
   * fetches the deployed app instead of replaying the version that broke.
   */
  private async discardServiceWorker(): Promise<void> {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(registration => registration.unregister()));
      }

      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys
          .filter(key => key.startsWith('ngsw:'))
          .map(key => caches.delete(key)));
      }
    } catch (err) {
      console.error('Failed to discard the service worker:', err);
    }
  }

  async ngOnInit(): Promise<void> {
    try {
      await this.auth.whenReady();
    } finally {
      // The app has to render even if resolving the identity went wrong.
      this.isIframe = window !== window.parent && !window.opener;
      this.loading = false;
    }
  }

  async logout() {
    this.authService.logoutRedirect();
  }

  isStaging() {
    return environment.isStaging;
  }
}
